import { Node } from '@tiptap/core';

const extractFeedItemInfoFromUrl = (input) => {
  if (!input) return null;

  const raw = input.toString().trim();
  if (!raw) return null;

  let url;
  try {
    url = new URL(raw, window.location.origin);
  } catch {
    return null;
  }

  const match = url.pathname.match(/^\/subscriber_v2\/feed_items\/(\d+)$/);
  if (!match) return null;

  const feedItemId = match[1];
  const timestamp = url.searchParams.get('timestamp');
  const timestampSeconds = timestamp && /^\d+$/.test(timestamp) ? parseInt(timestamp, 10) : null;

  return {
    feedItemId,
    timestampSeconds,
    href: url.pathname + url.search,
  };
};

const stripTimestampSuffix = (label) => {
  return (label || '').toString().replace(/\s*@\s*\d{1,2}:\d{2}(?::\d{2})?\s*$/, '').trim();
};

const formatSecondsToTimestamp = (seconds) => {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) return '';
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;

  const mm = m.toString().padStart(2, '0');
  const ss = s.toString().padStart(2, '0');

  if (h > 0) {
    const hh = h.toString().padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  }

  return `${m}:${ss}`;
};

const parseTimestampToSeconds = (input) => {
  const raw = (input || '').toString().trim();
  if (raw === '') return null;

  if (/^\d+$/.test(raw)) {
    const seconds = parseInt(raw, 10);
    return Number.isFinite(seconds) ? seconds : null;
  }

  if (!/^\d{1,2}:\d{2}(:\d{2})?$/.test(raw)) return null;

  const parts = raw.split(':').map((p) => parseInt(p, 10));
  if (parts.some((p) => !Number.isFinite(p))) return null;

  let seconds = 0;
  if (parts.length === 2) {
    const [m, s] = parts;
    seconds = (m * 60) + s;
  } else {
    const [h, m, s] = parts;
    seconds = (h * 3600) + (m * 60) + s;
  }

  return seconds;
};

const el = (tagName, { className, text, attrs, dataset } = {}) => {
  const node = document.createElement(tagName);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  if (attrs) {
    Object.entries(attrs).forEach(([k, v]) => {
      if (v == null) return;
      node.setAttribute(k, v);
    });
  }
  if (dataset) {
    Object.entries(dataset).forEach(([k, v]) => {
      if (v == null) return;
      node.dataset[k] = v;
    });
  }
  return node;
};

export const EpisodeReference = Node.create({
  name: 'episodeReference',

  priority: 1001,

  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      episodeId: { default: null },
      feedItemId: { default: null },
      label: { default: null },
      timestamp: { default: null },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'a[data-episode-reference]',
        priority: 1000,
        getAttrs: (dom) => {
          const anchor = dom;
          const info = extractFeedItemInfoFromUrl(anchor.getAttribute('href'));
          const episodeId = anchor.getAttribute('data-episode-id');
          const feedItemId = anchor.getAttribute('data-feed-item-id') || info?.feedItemId || null;
          const label = anchor.getAttribute('data-episode-label') || anchor.textContent;
          const timestamp = anchor.getAttribute('data-episode-timestamp') || (info ? `${info.timestampSeconds || ''}` : null);

          return {
            episodeId: episodeId,
            feedItemId: feedItemId,
            label: label,
            timestamp: timestamp ? parseInt(timestamp, 10) : null,
          };
        },
      },
      {
        tag: 'a[href]',
        priority: 900,
        getAttrs: (dom) => {
          const anchor = dom;
          if (anchor.getAttribute('data-episode-reference')) return false;

          const info = extractFeedItemInfoFromUrl(anchor.getAttribute('href'));
          if (!info) return false;

          const rawLabel = (anchor.textContent || '').toString();
          const label = stripTimestampSuffix(rawLabel);

          return {
            episodeId: null,
            feedItemId: info.feedItemId,
            label: label || rawLabel,
            timestamp: info.timestampSeconds,
          };
        },
      },
    ];
  },

  renderHTML({ node }) {
    const episodeId = node.attrs.episodeId;
    const feedItemId = node.attrs.feedItemId;
    const label = node.attrs.label;
    const timestamp = node.attrs.timestamp;

    const displayLabel = (typeof timestamp === 'number' && Number.isFinite(timestamp) && timestamp > 0)
      ? `${label} @ ${formatSecondsToTimestamp(timestamp)}`
      : label;

    let href = `/subscriber_v2/feed_items/${feedItemId || episodeId}`;
    if (typeof timestamp === 'number' && Number.isFinite(timestamp) && timestamp > 0) {
      href += `?timestamp=${timestamp}`;
    }

    const attrs = {
      href,
      class: 'underline bg-indigo-50 rounded py-0.5 px-1',
      contenteditable: 'false',
      draggable: 'false',
      'data-controller': 'play-button',
      'data-action': 'click->play-button#play',
      'data-episode-reference': 'true',
    };

    return ['a', attrs, displayLabel];
  },

  addNodeView() {
    return ({ node }) => {
      const episodeId = node.attrs.episodeId;
      const feedItemId = node.attrs.feedItemId;
      const label = node.attrs.label;
      const timestamp = node.attrs.timestamp;

      const displayLabel = (typeof timestamp === 'number' && Number.isFinite(timestamp) && timestamp > 0)
        ? `${label} @ ${formatSecondsToTimestamp(timestamp)}`
        : label;

      let href = `/subscriber_v2/feed_items/${feedItemId || episodeId}`;
      if (typeof timestamp === 'number' && Number.isFinite(timestamp) && timestamp > 0) {
        href += `?timestamp=${timestamp}`;
      }

      const dom = document.createElement('a');
      dom.setAttribute('href', href);
      dom.setAttribute('data-controller', 'play-button');
      dom.setAttribute('data-action', 'click->play-button#play');
      dom.setAttribute('data-episode-reference', 'true');

      dom.className = 'underline bg-indigo-50 rounded py-0.5 px-1';
      dom.contentEditable = 'false';
      dom.draggable = false;
      dom.textContent = displayLabel;

      return { dom };
    };
  },

  addKeyboardShortcuts() {
    const replaceRangeWithAt = (from, to) => {
      return this.editor
        .chain()
        .focus()
        .unsetAllMarks()
        .insertContentAt({ from, to }, { type: 'text', text: '@' })
        .run();
    };

    const maybeReplaceNodeBefore = () => {
      const { state } = this.editor;
      const { selection } = state;
      if (!selection.empty) return false;

      const { $from } = selection;
      const nodeBefore = $from.nodeBefore;
      if (!nodeBefore || nodeBefore.type.name !== 'episodeReference') return false;

      const from = $from.pos - nodeBefore.nodeSize;
      const to = $from.pos;
      return replaceRangeWithAt(from, to);
    };

    const maybeReplaceNodeAfter = () => {
      const { state } = this.editor;
      const { selection } = state;
      if (!selection.empty) return false;

      const { $from } = selection;
      const nodeAfter = $from.nodeAfter;
      if (!nodeAfter || nodeAfter.type.name !== 'episodeReference') return false;

      const from = $from.pos;
      const to = $from.pos + nodeAfter.nodeSize;
      return replaceRangeWithAt(from, to);
    };

    const maybeReplaceSelectedNode = () => {
      const { state } = this.editor;
      const { selection } = state;
      if (!selection.node || selection.node.type.name !== 'episodeReference') return false;
      return replaceRangeWithAt(selection.from, selection.to);
    };

    return {
      Backspace: () => maybeReplaceSelectedNode() || maybeReplaceNodeBefore(),
      Delete: () => maybeReplaceSelectedNode() || maybeReplaceNodeAfter(),
    };
  },
});

export class EpisodeMentionsFeature {
  constructor({ hostElement }) {
    this.hostElement = hostElement;
    this.editor = null;

    this.state = {
      active: false,
      mode: 'search',
      query: '',
      range: null,
      items: [],
      selectedIndex: 0,
      selectedItem: null,
      timestampSeconds: null,
      menuEl: null,
      debounceTimer: null,
      abortController: null,
      pointerDownInMenu: false,
      lastMenuPointerDownAt: 0,
    };

    this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
    document.addEventListener('pointerdown', this.onDocumentPointerDown, true);
  }

  setEditor(editor) {
    this.editor = editor;
  }

  destroy() {
    this.close();
    if (this.state.menuEl) {
      this.state.menuEl.remove();
      this.state.menuEl = null;
    }
    document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
  }

  onUpdate() {
    this.update();
  }

  onBlur() {
    this.deferClose();
  }

  onSelectionUpdate() {
    this.update();
  }

  handlePaste(view, event) {
    const clipboardData = event.clipboardData;
    if (!clipboardData) return false;

    const text = (clipboardData.getData('text/plain') || '').toString();
    const trimmed = text.trim();
    if (!trimmed) return false;

    const info = extractFeedItemInfoFromUrl(trimmed);
    if (!info) return false;

    if (trimmed !== info.href && trimmed !== new URL(info.href, window.location.origin).toString()) {
      return false;
    }

    event.preventDefault();

    const range = { from: view.state.selection.from, to: view.state.selection.to };
    this.insertFeedItemReference({
      feedItemId: info.feedItemId,
      timestampSeconds: info.timestampSeconds,
      range,
      fallbackText: trimmed,
    });

    return true;
  }

  handleKeyDown(_view, event) {
    if (!this.state.active) return false;

    if (this.state.mode === 'timestamp') {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.backToSearch();
        return true;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        this.insertSelected();
        return true;
      }

      return false;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.state.selectedIndex = Math.min(this.state.selectedIndex + 1, Math.max(this.state.items.length - 1, 0));
      this.renderMenu();
      return true;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.state.selectedIndex = Math.max(this.state.selectedIndex - 1, 0);
      this.renderMenu();
      return true;
    }

    if (event.key === 'Enter') {
      const item = this.state.items[this.state.selectedIndex];
      if (!item) return false;
      event.preventDefault();
      this.openTimestampPicker(item);
      return true;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return true;
    }

    return false;
  }

  insertFeedItemReference({ feedItemId, timestampSeconds, range, fallbackText }) {
    const attrs = {
      episodeId: null,
      feedItemId: `${feedItemId}`,
      label: stripTimestampSuffix((fallbackText || '').toString()),
      timestamp: (typeof timestampSeconds === 'number' && Number.isFinite(timestampSeconds)) ? timestampSeconds : null,
    };

    this.editor
      .chain()
      .focus()
      .unsetAllMarks()
      .insertContentAt(range, [
        { type: 'episodeReference', attrs },
        { type: 'text', text: ' ', marks: [] },
      ])
      .run();
  }

  onDocumentPointerDown(event) {
    if (!this.state.active) return;
    if (this.state.menuEl && this.state.menuEl.contains(event.target)) return;
    if (this.hostElement.contains(event.target)) return;
    this.close();
  }

  ensureMenu() {
    if (this.state.menuEl) return;

    const menu = el('div', {
      className: 'fixed min-w-[240px] max-w-[420px] overflow-auto bg-white border border-gray-200 rounded-md shadow-lg hidden',
    });

    menu.style.zIndex = '9999';

    menu.addEventListener('pointerdown', (e) => {
      this.state.pointerDownInMenu = true;
      this.state.lastMenuPointerDownAt = Date.now();

      const clearPointerDown = () => {
        this.state.pointerDownInMenu = false;
        window.removeEventListener('pointerup', clearPointerDown, true);
        window.removeEventListener('pointercancel', clearPointerDown, true);
      };

      window.addEventListener('pointerup', clearPointerDown, true);
      window.addEventListener('pointercancel', clearPointerDown, true);

      if (this.state.mode === 'search') {
        e.preventDefault();
      }
    });

    document.body.appendChild(menu);
    this.state.menuEl = menu;
  }

  update() {
    if (!this.editor) return;

    if (this.state.mode === 'timestamp') {
      this.positionMenu();
      return;
    }

    const { state } = this.editor;
    const { selection } = state;
    if (!selection.empty) {
      this.close();
      return;
    }

    const { $from } = selection;
    const textBefore = state.doc.textBetween($from.start(), $from.pos, "\n", "\0");

    const atIndex = textBefore.lastIndexOf('@');
    if (atIndex === -1) {
      this.close();
      return;
    }

    const charBefore = atIndex === 0 ? '' : textBefore[atIndex - 1];
    if (charBefore && !/\s/.test(charBefore)) {
      this.close();
      return;
    }

    const query = textBefore.slice(atIndex + 1);
    if (query.match(/\s/)) {
      this.close();
      return;
    }

    const from = $from.pos - query.length - 1;
    const to = $from.pos;
    const range = { from, to };

    const wasActive = this.state.active;
    const prevQuery = this.state.query;

    this.state.active = true;
    this.state.mode = 'search';
    this.state.query = query;
    this.state.range = range;

    if (!wasActive || prevQuery !== query) {
      this.state.selectedIndex = 0;
      this.fetch(query);
    }

    this.positionMenu();
  }

  fetch(query) {
    if (this.state.debounceTimer) {
      clearTimeout(this.state.debounceTimer);
    }

    this.state.debounceTimer = setTimeout(() => {
      if (this.state.abortController) {
        this.state.abortController.abort();
      }

      const controller = new AbortController();
      this.state.abortController = controller;

      const url = `/subscriber_v2/episodes/mentions?q=${encodeURIComponent(query)}`;
      fetch(url, {
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
        signal: controller.signal,
      })
      .then((r) => (r.ok ? r.json() : []))
      .then((items) => {
        if (!this.state.active) return;
        this.state.items = Array.isArray(items) ? items : [];
        this.state.selectedIndex = 0;
        this.renderMenu();
        this.positionMenu();
      })
      .catch((e) => {
        if (e?.name === 'AbortError') return;
        this.close();
      });
    }, 150);
  }

  renderMenu() {
    if (!this.state.active) return;
    this.ensureMenu();

    if (this.state.mode === 'timestamp') {
      this.renderTimestampPicker();
      return;
    }

    const menu = this.state.menuEl;
    const items = this.state.items || [];

    if (items.length === 0) {
      menu.replaceChildren();
      menu.classList.add('hidden');
      return;
    }

    const buttons = items.map((item, idx) => {
      const label = (item?.label || '').toString();
      const active = idx === this.state.selectedIndex;

      const btn = el('button', {
        className: `block w-full text-left px-3 py-2 text-sm text-gray-900 hover:bg-gray-100 ${active ? 'bg-gray-100' : ''}`.trim(),
        text: label,
        attrs: { type: 'button' },
      });

      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.openTimestampPicker(item);
      });

      return btn;
    });

    menu.replaceChildren(...buttons);
    menu.classList.remove('hidden');
  }

  renderTimestampPicker() {
    if (!this.state.active) return;
    if (this.state.mode !== 'timestamp') return;

    this.ensureMenu();
    const menu = this.state.menuEl;
    const item = this.state.selectedItem;

    if (!item) {
      this.backToSearch();
      return;
    }

    const label = (item.label || '').toString();
    const audioUrl = (item.audio_url || '').toString();
    const videoUrl = (item.video_url || '').toString();

    const container = el('div', { className: 'p-3' });
    const title = el('div', { className: 'text-sm font-semibold text-gray-900 mb-2', text: label });

    container.appendChild(title);

    let audio = null;
    let media = null;
    if (audioUrl) {
      audio = document.createElement('audio');
      audio.className = 'w-full mb-2';
      audio.controls = true;
      audio.preload = 'metadata';
      audio.src = audioUrl;
      container.appendChild(audio);

      media = audio;

      audio.addEventListener('timeupdate', () => {
        this.state.timestampSeconds = Math.floor(audio.currentTime || 0);
      });
      audio.addEventListener('seeked', () => {
        this.state.timestampSeconds = Math.floor(audio.currentTime || 0);
      });
    } else if (videoUrl) {
      const video = document.createElement('video');
      video.className = 'w-full mb-2';
      video.controls = true;
      video.preload = 'metadata';
      video.src = videoUrl;
      container.appendChild(video);

      media = video;

      video.addEventListener('timeupdate', () => {
        this.state.timestampSeconds = Math.floor(video.currentTime || 0);
      });
      video.addEventListener('seeked', () => {
        this.state.timestampSeconds = Math.floor(video.currentTime || 0);
      });
    }

    const row = el('div', { className: 'flex items-center gap-2 mb-3' });
    const input = el('input', {
      className: 'flex-1 border border-gray-200 rounded-md px-2 py-1 text-sm',
      attrs: {
        type: 'text',
        placeholder: 'Timestamp (optional) e.g. 1:23',
      },
      dataset: { episodeMentionTimestampInput: 'true' },
    });

    input.addEventListener('input', () => {
      const seconds = parseTimestampToSeconds(input.value);
      this.state.timestampSeconds = seconds;
      if (media && typeof seconds === 'number' && seconds >= 0) {
        media.currentTime = seconds;
      }
    });

    const useCurrent = el('button', {
      className: 'border border-gray-200 rounded-md px-2 py-1 text-sm bg-white text-gray-900 hover:bg-gray-50',
      text: 'Use current',
      attrs: { type: 'button' },
      dataset: { episodeMentionUseCurrent: 'true' },
    });

    useCurrent.addEventListener('click', () => {
      if (!media) return;
      const seconds = Math.floor(media.currentTime || 0);
      this.state.timestampSeconds = seconds;
      input.value = formatSecondsToTimestamp(seconds);
    });

    row.appendChild(input);
    row.appendChild(useCurrent);
    container.appendChild(row);

    const actions = el('div', { className: 'flex justify-end gap-2' });

    const back = el('button', {
      className: 'border border-gray-200 rounded-md px-2 py-1 text-sm bg-white text-gray-900 hover:bg-gray-50',
      text: 'Back',
      attrs: { type: 'button' },
      dataset: { episodeMentionBack: 'true' },
    });

    back.addEventListener('click', (e) => {
      e.preventDefault();
      this.backToSearch();
    });

    const skip = el('button', {
      className: 'border border-gray-200 rounded-md px-2 py-1 text-sm bg-white text-gray-900 hover:bg-gray-50',
      text: 'Skip',
      attrs: { type: 'button' },
      dataset: { episodeMentionSkip: 'true' },
    });

    skip.addEventListener('click', (e) => {
      e.preventDefault();
      this.state.timestampSeconds = null;
      this.insertSelected();
    });

    const insert = el('button', {
      className: 'border border-gray-900 rounded-md px-2 py-1 text-sm bg-gray-900 text-white hover:bg-gray-800',
      text: 'Insert',
      attrs: { type: 'button' },
      dataset: { episodeMentionInsert: 'true' },
    });

    insert.addEventListener('click', (e) => {
      e.preventDefault();
      this.insertSelected();
    });

    actions.appendChild(back);
    actions.appendChild(skip);
    actions.appendChild(insert);

    container.appendChild(actions);

    menu.replaceChildren(container);
    menu.classList.remove('hidden');
  }

  openTimestampPicker(item) {
    if (!item) return;
    if (!this.state.range) return;

    this.state.mode = 'timestamp';
    this.state.selectedItem = item;
    this.state.timestampSeconds = null;

    this.renderTimestampPicker();
    this.positionMenu();
  }

  backToSearch() {
    this.state.mode = 'search';
    this.state.selectedItem = null;
    this.state.timestampSeconds = null;
    this.renderMenu();
    this.positionMenu();
  }

  insertSelected() {
    const item = this.state.selectedItem;
    if (!item) return;
    this.insertEpisodeMention(item, this.state.timestampSeconds);
  }

  insertEpisodeMention(item, timestampSeconds = null) {
    if (!this.editor) return;
    if (!this.state.range) return;

    const id = item.id;
    const feedItemId = item.feed_item_id;
    const label = (item.label || '').toString();
    if (!id || !label) return;

    const range = this.state.range;

    this.editor
      .chain()
      .focus()
      .unsetAllMarks()
      .insertContentAt(range, [
        {
          type: 'episodeReference',
          attrs: {
            episodeId: id,
            feedItemId: feedItemId,
            label: label,
            timestamp: (typeof timestampSeconds === 'number' && Number.isFinite(timestampSeconds)) ? timestampSeconds : null,
          },
        },
        { type: 'text', text: ' ', marks: [] },
      ])
      .run();

    this.close();
  }

  positionMenu() {
    if (!this.state.active) return;
    if (!this.editor) return;

    this.ensureMenu();
    const menu = this.state.menuEl;
    if (menu.classList.contains('hidden')) return;

    const view = this.editor.view;
    const pos = this.editor.state.selection.from;
    const coords = view.coordsAtPos(pos);

    const margin = 8;
    const offset = 6;

    menu.style.left = `${coords.left}px`;
    menu.style.top = `${coords.bottom + offset}px`;

    const rect = menu.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = coords.left;
    left = Math.min(left, viewportWidth - rect.width - margin);
    left = Math.max(margin, left);

    let top = coords.bottom + offset;
    if (top + rect.height + margin > viewportHeight) {
      top = coords.top - rect.height - offset;
    }
    top = Math.max(margin, Math.min(top, viewportHeight - rect.height - margin));

    menu.style.left = `${left}px`;
    menu.style.top = `${top}px`;
    menu.style.maxHeight = `${Math.max(120, viewportHeight - top - margin)}px`;
  }

  deferClose() {
    if (!this.state.active) return;
    if (this.state.mode === 'timestamp') return;

    setTimeout(() => {
      if (!this.state.active) return;
      if (this.state.pointerDownInMenu) return;

      if (Date.now() - (this.state.lastMenuPointerDownAt || 0) < 400) {
        return;
      }

      const active = document.activeElement;
      if (this.state.menuEl && active && this.state.menuEl.contains(active)) {
        return;
      }

      this.close();
    }, 0);
  }

  close() {
    this.state.active = false;
    this.state.mode = 'search';
    this.state.query = '';
    this.state.range = null;
    this.state.items = [];
    this.state.selectedIndex = 0;
    this.state.selectedItem = null;
    this.state.timestampSeconds = null;

    if (this.state.debounceTimer) {
      clearTimeout(this.state.debounceTimer);
      this.state.debounceTimer = null;
    }

    if (this.state.abortController) {
      this.state.abortController.abort();
      this.state.abortController = null;
    }

    if (this.state.menuEl) {
      this.state.menuEl.replaceChildren();
      this.state.menuEl.classList.add('hidden');
    }
  }
}
